// substance/SubstanceLibraryView.tsx
//
// The Substance Library view.  Contains all state, derived values, handlers,
// the usage-tabs row, the page header (title + Upload/Add actions), the filter
// rail + toolbar + scrollable table, and the keyed SlSubstanceDrawer.
//
// Header placement rationale: keeping the title/Upload/Add header inside the
// view (rather than in the page wrapper) avoids threading an imperative
// "openAdd" ref or a callback prop through the wrapper.  SubstanceLibraryPage
// renders nothing extra — the ProtectedShell provides the sidebar and the
// Outlet; the view's own header fills the top of the Outlet.
//
// Infinite-scroll: the optional fetchNextPage / hasNextPage / isFetchingNextPage
// props attach an IntersectionObserver sentinel at the bottom of the scroll area.
// When the props are omitted, no sentinel is rendered.

import * as React from "react";
import { Icon } from "@/components/Icon/Icon";

import { applyFilters, defaultFilters } from "./filters";
import type { SubstanceFilters } from "./filters";
import { buildGroups } from "./grouping";
import type { GroupKey } from "./grouping";
import { useCopy } from "./atoms/useCopy";
import { blankSubstance } from "./drawer/blankSubstance";
import type { SubstanceRow, SubstanceUsage } from "./_model";

import { SubstanceUsageTabs } from "./SubstanceUsageTabs";
import { SubstanceToolbar } from "./SubstanceToolbar";
import { SubstanceFilterRail } from "./SubstanceFilterRail";
import { SubstanceTable } from "./SubstanceTable";
import { SlSubstanceDrawer } from "./drawer/SlSubstanceDrawer";
import type { DrawerCatalogs } from "./drawer/SlSubstanceDrawer";

// "console" preset default group-by
const DEFAULT_GROUP_BY: GroupKey = "code";

type EditingState =
  | { mode: "edit"; id: string }
  | { mode: "add"; draft: SubstanceRow };

export interface SubstanceLibraryViewProps {
  subs: SubstanceRow[];
  setSubs: React.Dispatch<React.SetStateAction<SubstanceRow[]>>;
  /** Called when the "Upload inventory" button is clicked (optional; button is
   *  rendered disabled with a "Coming soon" tooltip when omitted). */
  onUploadInventory?: () => void;
  // ── Infinite-scroll (all optional — omit for mock/dev route) ──────────────
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // ── Real-data persistence (Task B3) ───────────────────────────────────────
  // All optional: the live SubstanceLibraryPage supplies real implementations;
  // when they are omitted the view stays purely optimistic-local (no network).
  //
  // The view always keeps its optimistic `setSubs` mirror; when a persist prop
  // is present it ALSO routes the change to the backend.
  /** Diff-and-persist a drawer edit. */
  onPersistUpdate?: (id: string, next: SubstanceRow) => void;
  /** Persist a new substance; resolves to the created inventory id (so the
   *  drawer can re-key to edit mode against the real row) or null on failure. */
  onPersistCreate?: (next: SubstanceRow) => Promise<string | null>;
  /** Archive (delete) a substance on the backend. */
  onPersistDelete?: (id: string) => void;
  /** Add-mode CODE suggestion: debounced resolve against identifiers. When
   *  omitted (dev route) no network call fires. */
  onResolveCode?: (
    identifiers: Array<{ kind: string; value: string }>,
  ) => void;
  /** Notifies the owner which substance id is currently open in the drawer
   *  (null when closed). The live page uses this to guard its optimistic
   *  `subs` mirror against a refetch clobbering an in-flight edit. */
  onEditingChange?: (id: string | null) => void;
  /** Live catalogs for the drawer's pickers. Built by SubstanceLibraryPage from
   *  useInventoryCatalog / useInventorySuppliers; omitted on the DEV route so the
   *  drawer renders empty pickers and fires zero network calls. */
  drawerCatalogs?: DrawerCatalogs;
}

export function SubstanceLibraryView({
  subs,
  setSubs,
  onUploadInventory,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onPersistUpdate,
  onPersistCreate,
  onPersistDelete,
  onResolveCode,
  onEditingChange,
  drawerCatalogs,
}: SubstanceLibraryViewProps) {
  const [search, setSearch] = React.useState("");
  const [usageTab, setUsageTab] = React.useState<"all" | SubstanceUsage>("all");
  const [filters, setFilters] = React.useState<SubstanceFilters>(() => defaultFilters());
  const [groupBy, setGroupBy] = React.useState<GroupKey>(DEFAULT_GROUP_BY);
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [editing, setEditing] = React.useState<EditingState | null>(null);
  const [copied, copy] = useCopy();

  // ── derived ────────────────────────────────────────────────────────────────
  const filtered = React.useMemo(
    () => applyFilters(subs, { search, usageTab, filters }),
    [subs, search, usageTab, filters],
  );

  const groups = React.useMemo(
    () => buildGroups(filtered, groupBy),
    [filtered, groupBy],
  );

  // Re-seed expanded groups ONLY when the user changes the grouping dimension.
  // `groups` is intentionally omitted from deps: search/filter changes must NOT
  // reset expanded state (that would collapse groups mid-search).
  React.useEffect(() => {
    setExpanded(new Set(groups.filter(g => !g.single && !g.flat).map(g => g.key)));
  }, [groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real data arrives after mount (`subs` starts empty on the live page), so the
  // groupBy-change effect above seeds nothing on first load. Seed the default
  // expansion ONCE when data first appears. Guarded so search/filter never re-seeds.
  const seededOnDataRef = React.useRef(false);
  React.useEffect(() => {
    if (seededOnDataRef.current || subs.length === 0) return;
    seededOnDataRef.current = true;
    setExpanded(new Set(groups.filter(g => !g.single && !g.flat).map(g => g.key)));
  }, [subs.length, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── expand/collapse all ───────────────────────────────────────────────────
  const multiGroups = groups.filter(g => !g.single && !g.flat);
  const allExpanded = multiGroups.length > 0 && multiGroups.every(g => expanded.has(g.key));
  const toggleAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(multiGroups.map(g => g.key)));

  // ── handlers ──────────────────────────────────────────────────────────────
  const toggleGroup = (key: string) =>
    setExpanded(s => {
      const n = new Set(s);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const onSelect = (id: string) =>
    setSelected(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const openEdit = (s: SubstanceRow) => setEditing({ mode: "edit", id: s.id });
  const openAdd = () => setEditing({ mode: "add", draft: blankSubstance() });

  // Notify the owner which row is open (null when closed) so it can guard its
  // optimistic mirror. Add mode reports null until the row is created (its temp
  // id isn't a server row yet); createSubstance flips editing to {edit,id}.
  React.useEffect(() => {
    onEditingChange?.(editing?.mode === "edit" ? editing.id : null);
  }, [editing, onEditingChange]);

  const updateSubstance = (id: string, next: SubstanceRow) => {
    // Optimistic local mirror (keeps the table + drawer in sync immediately).
    setSubs(arr => arr.map(s => (s.id === id ? next : s)));
    // Persist to the backend when wired (live page); no-op on the dev route.
    onPersistUpdate?.(id, next);
  };

  const createSubstance = (next: SubstanceRow) => {
    // Optimistic insert with the drawer-minted temp id.
    setSubs(arr => [next, ...arr]);
    if (onPersistCreate) {
      // Live path: keep the drawer in ADD mode until the server returns the real
      // inventory id. Flipping to edit-mode on the temp id now would route any
      // edit typed during the create round-trip to PATCH /me/inventory/<tempId>
      // (failed requests + lost input). In add mode the drawer's edits stay local.
      void onPersistCreate(next).then((realId) => {
        if (!realId) {
          // Create failed — drop the optimistic row; the drawer stays in add mode
          // (draft intact) so the user can retry.
          setSubs(arr => arr.filter(s => s.id !== next.id));
          return;
        }
        setSubs(arr => arr.map(s => (s.id === next.id ? { ...s, id: realId } : s)));
        setEditing(cur =>
          // Only flip if the user is still in the add flow for this draft.
          cur && cur.mode === "add" ? { mode: "edit", id: realId } : cur,
        );
      });
    } else {
      // Mock/dev path: no backend, so flip straight to edit on the temp id.
      setEditing({ mode: "edit", id: next.id });
    }
  };

  const deleteSubstance = (id: string) => {
    setSubs(arr => arr.filter(s => s.id !== id));
    setEditing(null);
    onPersistDelete?.(id);
  };

  // ── table ctx ──────────────────────────────────────────────────────────────
  const ctx = { selected, onSelect, onView: openEdit, copied, copy, groupBy };

  // ── infinite-scroll sentinel ───────────────────────────────────────────────
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!fetchNextPage || !hasNextPage) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isFetchingNextPage) fetchNextPage();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const hasSentinel = fetchNextPage !== undefined && hasNextPage;

  return (
    <>
      {/* ── Header: title + Upload + Add ─────────────────────────────────── */}
      <div
        className="hstack"
        style={{
          padding: "0 18px",
          minHeight: 56,
          borderBottom: "1px solid var(--line)",
          background: "var(--panel)",
          flexShrink: 0,
          gap: 16,
        }}
      >
        <span className="display" style={{ fontSize: 16 }}>
          Substance Library
        </span>
        <div style={{ flex: 1 }} />
        <button
          className="btn sm"
          disabled={!onUploadInventory}
          title={onUploadInventory ? undefined : "Coming soon"}
          onClick={onUploadInventory}
        >
          <Icon name="upload" size={12} />
          Upload inventory
        </button>
        <button className="btn primary sm" onClick={openAdd}>
          <Icon name="plus" size={12} />
          Add substance
        </button>
      </div>

      {/* ── Usage tabs row ───────────────────────────────────────────────── */}
      <div
        className="hstack"
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid var(--line)",
          background: "var(--panel-2)",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <SubstanceUsageTabs items={subs} value={usageTab} onChange={setUsageTab} />
        <div style={{ flex: 1 }} />

        {selected.size > 0 && (
          <div className="hstack" style={{ gap: 8 }}>
            <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
              {selected.size} selected
            </span>
            <button className="btn xs">
              <Icon name="bookmark" size={10} />
              Tag
            </button>
            <button className="btn xs">
              <Icon name="download" size={10} />
              Export
            </button>
            <button className="btn xs ghost" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <SubstanceFilterRail items={subs} filters={filters} setFilters={setFilters} />

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SubstanceToolbar
            search={search}
            onSearch={setSearch}
            groupBy={groupBy}
            onGroupBy={setGroupBy}
            shown={filtered.length}
            total={subs.length}
            allExpanded={allExpanded}
            onToggleAll={toggleAll}
            showExpandAll={multiGroups.length > 0}
          />

          <div style={{ flex: 1, overflow: "auto", background: "var(--panel)" }}>
            {(usageTab === "target" || usageTab === "formulation") && filtered.length === 0 ? (
              // Targets & user formulations are flavor-scoped today (not standalone
              // library entries) — surface that honestly rather than show a blank list.
              <div style={{ padding: "70px 20px", textAlign: "center", color: "var(--ink-4)" }}>
                <div style={{ marginBottom: 10 }}>
                  <Icon name={usageTab === "target" ? "target" : "beaker"} size={26} />
                </div>
                <div style={{ fontSize: 13, color: "var(--ink-3)" }}>
                  {usageTab === "target" ? "Targets" : "User formulations"} live inside flavors today.
                </div>
                <div
                  className="mono"
                  style={{ fontSize: 11, marginTop: 4, maxWidth: 380, marginInline: "auto", lineHeight: 1.5 }}
                >
                  Standalone {usageTab === "target" ? "target" : "formulation"} entries in the Substance
                  Library are coming soon.
                </div>
              </div>
            ) : (
              <>
                <SubstanceTable
                  items={filtered}
                  groups={groups}
                  groupBy={groupBy}
                  expanded={expanded}
                  onToggleGroup={toggleGroup}
                  ctx={ctx}
                />

                {/* Infinite-scroll sentinel (only when real data props are provided) */}
                {hasSentinel && <div ref={sentinelRef} style={{ height: 1 }} />}

                {isFetchingNextPage && (
                  <div
                    className="mono"
                    style={{ padding: "12px 20px", fontSize: 11, color: "var(--ink-4)", textAlign: "center" }}
                  >
                    Loading more…
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Drawer — keyed per substance to prevent state bleed ─────────── */}
      {editing && (() => {
        const sub =
          editing.mode === "add"
            ? editing.draft
            : subs.find(s => s.id === editing.id);
        if (!sub) return null;
        return (
          <SlSubstanceDrawer
            key={editing.mode === "add" ? "sl-add" : editing.id}
            mode={editing.mode}
            substance={sub}
            onClose={() => setEditing(null)}
            onUpdate={updateSubstance}
            onCreate={createSubstance}
            onDelete={deleteSubstance}
            onResolveCode={onResolveCode}
            catalogs={drawerCatalogs}
          />
        );
      })()}
    </>
  );
}
